import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  History,
  Settings,
  LogOut,
  Menu,
  X,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Sun,
  Moon,
  Laptop,
  Plus,
  MoreVertical,
} from 'lucide-react';
import { AppMark } from './Logo';
import { Button, ConfirmDialog, Modal } from './UIComponents';
import { useTheme } from './ThemeProvider';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useBilling';
import * as transcriptionsApi from '../api/transcriptions';
import { handleApiError } from '../api/client';

interface LayoutProps {
  children: React.ReactNode;
  variant: 'public' | 'app' | 'fullscreen';
}

const Layout: React.FC<LayoutProps> = ({ children, variant }) => {
  if (variant === 'public') return <PublicLayout>{children}</PublicLayout>;
  if (variant === 'fullscreen') return <FullscreenLayout>{children}</FullscreenLayout>;
  return <AppLayout>{children}</AppLayout>;
};

interface FolderRowMenuProps {
  onRename: () => void;
  onDelete: () => void;
}

const FolderRowMenu: React.FC<FolderRowMenuProps> = ({ onRename, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const updateMenuPosition = useCallback((menuHeight?: number) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    const menuWidth = 176; // w-44
    const height = menuHeight ?? 88; // ~2 rows
    const margin = 4;
    const padding = 8;
    const shouldOpenUp = rect.bottom + height + margin > window.innerHeight - padding;
    const top = shouldOpenUp
      ? Math.max(padding, rect.top - height - margin)
      : rect.bottom + margin;
    const left = Math.max(padding, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - padding));
    setMenuPosition({ top, left, openUp: shouldOpenUp });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && menuRef.current.contains(target)) return;
      if (buttonRef.current && buttonRef.current.contains(target)) return;
      if (isOpen) {
        setIsOpen(false);
      }
    };

    const handleWindowChanged = () => {
      if (!isOpen) return;
      updateMenuPosition(menuRef.current?.offsetHeight);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleWindowChanged);
    window.addEventListener('scroll', handleWindowChanged, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleWindowChanged);
      window.removeEventListener('scroll', handleWindowChanged, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) {
      setIsMenuVisible(false);
      return;
    }
    const raf = window.requestAnimationFrame(() => {
      updateMenuPosition(menuRef.current?.offsetHeight);
      setIsMenuVisible(true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [isOpen, updateMenuPosition]);

  return (
    <div className="relative">
      <button
        type="button"
        className="text-ink-3 hover:text-ink p-1 rounded-md hover:bg-black/[0.05] dark:hover:bg-white/[0.08]"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => {
            const next = !prev;
            if (next) {
              updateMenuPosition();
            }
            return next;
          });
        }}
        aria-label="Folder actions"
        ref={buttonRef}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          className={`fixed w-44 bg-raised rounded-[14px] shadow-pop p-1 z-[9999] whitespace-nowrap flex flex-col transform transition duration-150 ease-out ${
            isMenuVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          } ${menuPosition.openUp ? 'origin-bottom-right' : 'origin-top-right'}`}
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
            <button
              type="button"
              className="block w-full rounded-[10px] px-3 py-1.5 text-start text-[13.5px] text-ink hover:bg-card whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
                onRename();
              }}
            >
              {t('common.rename')}
            </button>
            <button
              type="button"
              className="block w-full rounded-[10px] px-3 py-1.5 text-start text-[13.5px] text-[#ff3b30] dark:text-[#ff453a] hover:bg-[#ff3b30]/10 whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
                onDelete();
              }}
            >
              {t('common.delete')}
            </button>
        </div>,
        document.body
      )}
    </div>
  );
};

// --- Shared chrome ---
const themeOrder = ['light', 'dark', 'system'] as const;
const themeIcon = { light: Sun, dark: Moon, system: Laptop };

const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const Icon = themeIcon[theme];
  const next = themeOrder[(themeOrder.indexOf(theme) + 1) % themeOrder.length];
  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      className="grid h-8 w-8 place-items-center rounded-full text-ink-2 transition-colors hover:bg-card hover:text-ink"
      aria-label={`${t('theme.title')}: ${t(`theme.${theme}`)}`}
      title={`${t('theme.title')}: ${t(`theme.${theme}`)}`}
    >
      <Icon className="h-[15px] w-[15px]" />
    </button>
  );
};

const ThemeSegmented: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('theme.title')} className="seg-group">
      {themeOrder.map((value) => {
        const Icon = themeIcon[value];
        return (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            aria-pressed={theme === value}
            title={t(`theme.${value}`)}
            className="seg !px-2.5 !py-1"
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
};

// --- Public Layout (Header + Footer) ---
const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // Hide nav buttons on auth pages to reduce distraction
  const isAuthPage = ['/login', '/register', '/forgot-password'].includes(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMobileMenuOpen(false), [location.pathname]);

  const navigateToHomeSection = (sectionId: string) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
      window.setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      return;
    }
    window.history.replaceState(null, '', `/#${sectionId}`);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const linkClass = (active = false) =>
    `whitespace-nowrap transition-colors ${active ? 'text-ink' : 'text-ink-2 hover:text-ink'}`;

  const footerColumns: { title: string; links: { label: string; to: string; external?: boolean }[] }[] = [
    {
      title: t('footer.product'),
      links: [
        { label: t('footer.transcribe'), to: '/' },
        { label: 'Transcribe audio to text', to: '/transcribe-audio-to-text' },
        { label: t('footer.pricing'), to: '/pricing' },
        { label: t('nav.api'), to: '/api' },
      ],
    },
    {
      title: t('nav.tools'),
      links: [
        { label: t('nav.audioConverter'), to: '/tools/audio-converter' },
        { label: t('nav.opusConverter'), to: '/tools/opus-converter' },
        { label: t('footer.documentation'), to: '/api' },
      ],
    },
    {
      title: t('footer.company'),
      links: [
        { label: t('footer.about'), to: '/about' },
        { label: t('footer.contact'), to: 'mailto:support@omniscribe.ai', external: true },
        { label: t('footer.security'), to: '/security' },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.termsOfService'), to: '/terms' },
        { label: t('footer.privacyPolicy'), to: '/privacy' },
        { label: t('footer.refundPolicy'), to: '/refund-policy' },
        { label: t('footer.cookiePolicy'), to: '/privacy' },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-bg text-ink">
      <header
        className={`sticky top-0 z-40 w-full bg-bg/80 backdrop-blur-xl backdrop-saturate-150 transition-shadow duration-300 ${
          scrolled || mobileMenuOpen ? 'shadow-[0_1px_0_var(--line)]' : ''
        }`}
      >
        <nav className="shell flex h-16 items-center gap-4 lg:gap-8" aria-label="Main">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <AppMark size={26} />
            <span className="text-[16px] font-semibold tracking-[-0.01em]">OmniScribe</span>
          </Link>

          {!isAuthPage && (
            <div className="hidden items-center gap-7 text-[13.5px] lg:flex">
              <button type="button" onClick={() => navigateToHomeSection('features')} className={linkClass()}>
                {t('nav.features')}
              </button>
              <Link to="/pricing" className={linkClass(location.pathname.startsWith('/pricing'))}>{t('nav.pricing')}</Link>
              <button type="button" onClick={() => navigateToHomeSection('faq')} className={linkClass()}>
                {t('nav.faq')}
              </button>
              <div className="group relative">
                <button type="button" className={`flex items-center gap-1 ${linkClass(location.pathname.startsWith('/tools'))}`}>
                  {t('nav.tools')}
                  <ChevronDown className="h-3 w-3 opacity-60 transition-transform group-hover:rotate-180" />
                </button>
                <div className="invisible absolute start-1/2 top-full z-50 w-56 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 rtl:translate-x-1/2">
                  <div className="flex flex-col rounded-[18px] bg-raised p-1.5 shadow-pop">
                    {[
                      { to: '/tools/audio-converter', label: t('nav.audioConverter') },
                      { to: '/tools/opus-converter', label: t('nav.opusConverter') },
                    ].map((item) => (
                      <Link key={item.to} to={item.to} className="rounded-[12px] px-3.5 py-2 text-[14px] text-ink-2 transition-colors hover:bg-card hover:text-ink">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              <Link to="/api" className={linkClass(location.pathname.startsWith('/api'))}>{t('nav.api')}</Link>
            </div>
          )}

          <div className="ms-auto flex items-center gap-1">
            <div className="hidden items-center gap-0.5 sm:flex">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>

            {isAuthPage ? (
              <div className="ms-3 text-[13.5px] text-ink-2">
                <span className="hidden sm:inline">{location.pathname === '/login' ? t('auth.noAccount') : t('auth.haveAccount')}</span>
                <Link
                  to={location.pathname === '/login' ? '/register' : '/login'}
                  className="ms-2 font-medium text-accent hover:underline underline-offset-4"
                >
                  {location.pathname === '/login' ? t('auth.signUp') : t('auth.signIn')}
                </Link>
              </div>
            ) : (
              <>
                {isAuthenticated && user ? (
                  <Link to="/dashboard" className="btn btn-sm ms-3 hidden lg:inline-flex">
                    <LayoutDashboard className="h-4 w-4" />
                    {t('nav.dashboard')}
                  </Link>
                ) : (
                  <div className="ms-3 hidden items-center gap-4 lg:flex">
                    <Link to="/login" className="link-quiet text-[13.5px] font-medium">{t('common.login')}</Link>
                    <Link to="/register" className="btn btn-sm">{t('auth.startForFree')}</Link>
                  </div>
                )}
                <button
                  type="button"
                  className="grid h-9 w-9 place-items-center rounded-full text-ink transition-colors hover:bg-card lg:hidden"
                  onClick={() => setMobileMenuOpen((open) => !open)}
                  aria-label="Menu"
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </>
            )}
          </div>
        </nav>

        {/* Mobile Nav */}
        {mobileMenuOpen && !isAuthPage && (
          <div className="lg:hidden border-t border-line bg-bg dropdown-animate">
            <div className="shell flex flex-col py-4">
              {[
                { label: t('nav.features'), onClick: () => navigateToHomeSection('features') },
                { label: t('nav.pricing'), onClick: () => navigate('/pricing') },
                { label: t('nav.faq'), onClick: () => navigateToHomeSection('faq') },
                { label: t('nav.audioConverter'), onClick: () => navigate('/tools/audio-converter') },
                { label: t('nav.opusConverter'), onClick: () => navigate('/tools/opus-converter') },
                { label: t('nav.api'), onClick: () => navigate('/api') },
              ].map((item) => (
                <button key={item.label} type="button" onClick={item.onClick} className="flex items-center justify-between border-b border-line py-3.5 text-start text-[17px] font-medium">
                  {item.label}
                  <ChevronRight className="h-4 w-4 text-ink-3 rtl:rotate-180" />
                </button>
              ))}

              <div className="flex items-center justify-between py-4">
                <span className="text-[14px] text-ink-2">{t('theme.title')}</span>
                <ThemeSegmented />
              </div>
              <LanguageSwitcher variant="mobile" />

              <div className="mt-6 grid gap-2.5">
                {isAuthenticated && user ? (
                  <Link to="/dashboard" className="btn w-full">{t('nav.dashboard')}</Link>
                ) : (
                  <>
                    <Link to="/register" className="btn w-full">{t('auth.getStartedFree')}</Link>
                    <Link to="/login" className="btn btn-quiet w-full">{t('common.login')}</Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        {children}
      </main>

      <footer className="bg-card">
        <div className="shell py-14">
          <div className="grid gap-10 md:grid-cols-[1.4fr_repeat(4,1fr)]">
            <div className="max-w-xs">
              <Link to="/" className="flex items-center gap-2">
                <AppMark size={24} />
                <span className="text-[15px] font-semibold tracking-[-0.01em]">{t('common.appName')}</span>
              </Link>
              <p className="mt-3 text-[13px] leading-relaxed text-ink-2">
                {t('home.heroSubtitle')}
              </p>
            </div>
            {footerColumns.map((column) => (
              <div key={column.title}>
                <h3 className="text-[12.5px] font-semibold text-ink">{column.title}</h3>
                <ul className="mt-3 space-y-2.5">
                  {column.links.map((link) => (
                    <li key={`${column.title}-${link.label}`}>
                      {link.external ? (
                        <a href={link.to} className="link-quiet text-[13px]">{link.label}</a>
                      ) : (
                        <Link to={link.to} className="link-quiet text-[13px]">{link.label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-col gap-4 border-t border-line pt-6 text-[12px] text-ink-3 md:flex-row md:items-center md:justify-between">
            <p className="max-w-3xl leading-relaxed">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
            <div className="flex items-center gap-1 sm:hidden">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// --- App Layout (Sidebar + Topbar) ---
const UserAvatar: React.FC<{ url: string; showImage: boolean; initials: string; name?: string; onError: () => void; size?: string }> = ({
  url,
  showImage,
  initials,
  name,
  onError,
  size = 'h-8 w-8',
}) => (
  <div className={`${size} shrink-0 overflow-hidden rounded-full bg-gradient-to-b from-[#8e8e93] to-[#6e6e73] text-white flex items-center justify-center text-[11px] font-semibold`}>
    {showImage ? (
      <img
        src={url}
        alt={`${name || 'User'} avatar`}
        referrerPolicy="no-referrer"
        loading="lazy"
        decoding="async"
        onError={onError}
        className="h-full w-full object-cover"
      />
    ) : (
      initials
    )}
  </div>
);

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { user, logout } = useAuth();
  const { data: subscription } = useSubscription();
  const { t } = useTranslation();
  const [transcriptionFolders, setTranscriptionFolders] = useState<transcriptionsApi.TranscriptionFolder[]>([]);
  const [isTranscriptionFoldersLoading, setIsTranscriptionFoldersLoading] = useState(false);
  const [isTranscriptionsExpanded, setIsTranscriptionsExpanded] = useState(true);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [folderFormError, setFolderFormError] = useState<string | null>(null);
  const [renameFolder, setRenameFolder] = useState<transcriptionsApi.TranscriptionFolder | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [deleteFolder, setDeleteFolder] = useState<transcriptionsApi.TranscriptionFolder | null>(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [failedAvatarUrls, setFailedAvatarUrls] = useState<Record<string, boolean>>({});

  const folderNameMaxLength = 40;
  const isFolderLimitReached = transcriptionFolders.length >= 10;
  const newFolderNameLength = useMemo(() => Array.from(newFolderName).length, [newFolderName]);
  const renameFolderNameLength = useMemo(() => Array.from(renameFolderName).length, [renameFolderName]);

  const notifyFoldersChanged = useCallback(() => {
    window.dispatchEvent(new Event('transcription-folders:changed'));
  }, []);

  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
    navigate('/');
  };

  const markAvatarFailed = useCallback((avatarUrl?: string) => {
    if (!avatarUrl) {
      return;
    }
    setFailedAvatarUrls((prev) => {
      if (prev[avatarUrl]) {
        return prev;
      }
      return { ...prev, [avatarUrl]: true };
    });
  }, []);

  const userAvatarUrl = user?.avatar_url || '';
  const shouldShowAvatarImage = !!userAvatarUrl && !failedAvatarUrls[userAvatarUrl];

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  };

  // Get subscription tier display name
  const getTierName = () => {
    if (!subscription?.plan) return t('app.layout.sidebar.user.freePlan');
    return subscription.plan.name || t('app.layout.sidebar.user.freePlan');
  };

  const navItems = [
    { icon: LayoutDashboard, label: t('nav.overview'), path: '/dashboard' },
    { icon: History, label: t('nav.transcriptions'), path: '/history' },
    { icon: CreditCard, label: t('nav.billing'), path: '/billing' },
    { icon: Settings, label: t('nav.settings'), path: '/settings' },
  ];

  const isTranscriptionsActive = location.pathname === '/history' || location.pathname.startsWith('/transcription/');
  const currentFolderParam = new URLSearchParams(location.search).get('folder') || 'all';

  useEffect(() => {
    const stored = localStorage.getItem('omniscribe_transcriptions_expanded');
    if (stored === 'true' || stored === 'false') {
      setIsTranscriptionsExpanded(stored === 'true');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('omniscribe_transcriptions_expanded', String(isTranscriptionsExpanded));
  }, [isTranscriptionsExpanded]);

  useEffect(() => setSidebarOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const load = async () => {
      setIsTranscriptionFoldersLoading(true);
      try {
        const foldersResp = await transcriptionsApi.listTranscriptionFolders();
        if (!cancelled) {
          setTranscriptionFolders(foldersResp.folders || []);
        }
      } catch {
        if (!cancelled) {
          setTranscriptionFolders([]);
        }
      } finally {
        if (!cancelled) {
          setIsTranscriptionFoldersLoading(false);
        }
      }
    };

    load();
    const onChanged = () => load();
    window.addEventListener('transcription-folders:changed', onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('transcription-folders:changed', onChanged);
    };
  }, [user]);

  const openCreateFolder = useCallback(() => {
    setFolderFormError(null);
    setNewFolderName('');
    setIsCreateFolderOpen(true);
  }, []);

  const handleCreateFolder = useCallback(async () => {
    if (isFolderLimitReached) {
      setFolderFormError('Folder limit reached (max 10)');
      return;
    }
    const trimmed = newFolderName.trim();
    if (!trimmed) return;

    setIsCreatingFolder(true);
    setFolderFormError(null);
    try {
      await transcriptionsApi.createTranscriptionFolder(trimmed);
      setIsCreateFolderOpen(false);
      setNewFolderName('');
      notifyFoldersChanged();
    } catch (err) {
      setFolderFormError(handleApiError(err));
    } finally {
      setIsCreatingFolder(false);
    }
  }, [isFolderLimitReached, newFolderName, notifyFoldersChanged]);

  const handleRenameFolder = useCallback(async () => {
    if (!renameFolder) return;
    const trimmed = renameFolderName.trim();
    if (!trimmed) return;

    setIsRenamingFolder(true);
    setFolderFormError(null);
    try {
      await transcriptionsApi.renameTranscriptionFolder(renameFolder.id, trimmed);
      setRenameFolder(null);
      setRenameFolderName('');
      notifyFoldersChanged();
    } catch (err) {
      setFolderFormError(handleApiError(err));
    } finally {
      setIsRenamingFolder(false);
    }
  }, [notifyFoldersChanged, renameFolder, renameFolderName]);

  const handleDeleteFolder = useCallback(async () => {
    if (!deleteFolder) return;
    setIsDeletingFolder(true);
    try {
      await transcriptionsApi.deleteTranscriptionFolder(deleteFolder.id);
      if (isTranscriptionsActive && currentFolderParam === deleteFolder.id) {
        navigate('/history?folder=uncategorized', { replace: true });
      }
      setDeleteFolder(null);
      notifyFoldersChanged();
    } catch (err) {
      setDeleteFolder(null);
      // Best-effort: keep UI responsive even if delete fails.
      console.error(err);
    } finally {
      setIsDeletingFolder(false);
    }
  }, [currentFolderParam, deleteFolder, isTranscriptionsActive, navigate, notifyFoldersChanged]);

  const handleDropTranscriptionToFolder = useCallback(async (folderId: string | null, transcriptionId: string) => {
    if (!transcriptionId) return;
    try {
      await transcriptionsApi.moveTranscription(transcriptionId, folderId);
      notifyFoldersChanged();
      window.dispatchEvent(new Event('transcriptions:changed'));
    } catch (err) {
      console.error(err);
    }
  }, [notifyFoldersChanged]);

  const folderDropProps = (id: string | null) => {
    const key = id ?? 'uncategorized';
    return {
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        setDragOverFolderId(key);
      },
      onDragLeave: () => setDragOverFolderId(null),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const transcriptionId = e.dataTransfer.getData('text/plain');
        setDragOverFolderId(null);
        void handleDropTranscriptionToFolder(id, transcriptionId);
      },
    };
  };

  const navRowClass = (active: boolean) =>
    `group/nav flex w-full items-center rounded-[10px] transition-colors ${
      active
        ? 'bg-raised text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_0.5px_rgba(0,0,0,0.04)] dark:bg-white/[0.09] dark:shadow-none'
        : 'text-ink-2 hover:bg-black/[0.04] hover:text-ink dark:hover:bg-white/[0.05]'
    }`;

  const folderRowClass = (active: boolean, key: string) =>
    `w-full truncate rounded-[8px] px-2.5 py-1.5 text-start text-[13px] transition-colors ${
      active ? 'bg-black/[0.06] text-ink font-medium dark:bg-white/[0.08]' : 'text-ink-2 hover:bg-black/[0.04] hover:text-ink dark:hover:bg-white/[0.05]'
    } ${dragOverFolderId === key ? 'ring-2 ring-accent/50' : ''}`;

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <AppMark size={24} />
          <span className="text-[15px] font-semibold tracking-[-0.01em] text-ink">OmniScribe</span>
        </Link>
        <button type="button" className="grid h-8 w-8 place-items-center rounded-full text-ink-2 hover:bg-black/[0.05] lg:hidden" onClick={() => setSidebarOpen(false)} aria-label={t('common.close')}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-3 pb-3">
        <button type="button" onClick={() => navigate('/upload')} className="btn btn-sm w-full !justify-start !rounded-[10px] !py-2">
          <Plus className="h-4 w-4" />
          {t('dashboard.newTranscription')}
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden px-3 py-1">
        {navItems.map((item) => {
          const isHistoryItem = item.path === '/history';
          const isActive = isHistoryItem ? isTranscriptionsActive && currentFolderParam === 'all' && location.pathname === '/history' : location.pathname === item.path;
          return (
            <div key={item.path}>
              <div className={navRowClass(isActive)}>
                <button
                  type="button"
                  onClick={() => navigate(isHistoryItem ? '/history?folder=all' : item.path)}
                  className="flex flex-1 items-center gap-2.5 px-2.5 py-[7px] text-start text-[13.5px] font-medium"
                >
                  <item.icon className={`h-[17px] w-[17px] ${isActive ? 'text-accent' : ''}`} />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                </button>

                {isHistoryItem && (
                  <div className="flex items-center pe-1 opacity-70 group-hover/nav:opacity-100">
                    <button
                      type="button"
                      className={`rounded-md p-1 transition-colors ${
                        isFolderLimitReached ? 'cursor-not-allowed text-ink-3/50' : 'text-ink-3 hover:bg-black/[0.06] hover:text-ink dark:hover:bg-white/[0.08]'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isFolderLimitReached) return;
                        openCreateFolder();
                      }}
                      aria-label={t('app.layout.sidebar.folder.actions.create')}
                      title={isFolderLimitReached ? t('app.layout.sidebar.folder.limitReached') : t('app.layout.sidebar.folder.actions.create')}
                      disabled={isFolderLimitReached}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded-md p-1 text-ink-3 transition-colors hover:bg-black/[0.06] hover:text-ink dark:hover:bg-white/[0.08]"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsTranscriptionsExpanded((prev) => !prev);
                      }}
                      aria-label={isTranscriptionsExpanded ? 'Collapse transcriptions' : 'Expand transcriptions'}
                      aria-expanded={isTranscriptionsExpanded}
                    >
                      <ChevronRight className={`h-3.5 w-3.5 transition-transform rtl:rotate-180 ${isTranscriptionsExpanded ? 'rotate-90 rtl:rotate-90' : ''}`} />
                    </button>
                  </div>
                )}
              </div>

              {isHistoryItem && isTranscriptionsExpanded && (
                <div className="mb-1 ms-[30px] mt-0.5 space-y-0.5 border-s border-line ps-2">
                  <button
                    type="button"
                    onClick={() => navigate('/history?folder=uncategorized')}
                    className={folderRowClass(location.pathname === '/history' && currentFolderParam === 'uncategorized', 'uncategorized')}
                    title={t('common.uncategorized')}
                    {...folderDropProps(null)}
                  >
                    {t('common.uncategorized')}
                  </button>
                  {isTranscriptionFoldersLoading && transcriptionFolders.length === 0 ? (
                    <div className="px-2.5 py-1.5 text-[12.5px] text-ink-3">{t('common.loading')}</div>
                  ) : (
                    transcriptionFolders.map((f) => (
                      <div key={f.id} className="group/folder relative w-full">
                        <button
                          type="button"
                          onClick={() => navigate(`/history?folder=${encodeURIComponent(f.id)}`)}
                          className={`${folderRowClass(location.pathname === '/history' && currentFolderParam === f.id, f.id)} pe-8`}
                          title={f.name}
                          {...folderDropProps(f.id)}
                        >
                          {f.name}
                        </button>
                        <div className="absolute end-0.5 top-1/2 -translate-y-1/2 opacity-0 transition-opacity focus-within:opacity-100 group-hover/folder:opacity-100 max-lg:opacity-100">
                          <FolderRowMenu
                            onRename={() => {
                              setFolderFormError(null);
                              setRenameFolder(f);
                              setRenameFolderName(f.name);
                            }}
                            onDelete={() => setDeleteFolder(f)}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="space-y-3 p-3">
        <div className="flex items-center justify-between px-1">
          <span className="text-[12px] font-medium text-ink-3">{t('theme.title')}</span>
          <ThemeSegmented />
        </div>

        <div className="flex items-center gap-1 rounded-[12px] bg-raised p-1.5 ring-1 ring-black/[0.04] dark:bg-white/[0.05] dark:ring-white/[0.06]">
          <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[8px] p-1 text-start hover:bg-black/[0.03] dark:hover:bg-white/[0.04]" onClick={() => navigate('/settings')}>
            <UserAvatar url={userAvatarUrl} showImage={shouldShowAvatarImage} initials={getUserInitials()} name={user?.name} onError={() => markAvatarFailed(userAvatarUrl)} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium text-ink">{user?.name || 'User'}</span>
              <span className="block truncate text-[11.5px] text-ink-3">{getTierName()}</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => setShowLogoutConfirm(true)}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] text-ink-3 transition-colors hover:bg-black/[0.05] hover:text-ink dark:hover:bg-white/[0.08]"
            title={t('app.layout.sidebar.actions.signOut')}
            aria-label={t('app.layout.sidebar.actions.signOut')}
          >
            <LogOut className="h-4 w-4 rtl:rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:block w-[248px] fixed start-0 inset-y-0 z-30 bg-card/80 border-e border-line overflow-x-hidden">
        {sidebar}
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ms-[248px] flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden h-14 bg-bg/80 backdrop-blur-xl shadow-[0_1px_0_var(--line)] flex items-center justify-between px-3 sticky top-0 z-20">
          <div className="flex items-center gap-1.5">
            <button type="button" className="grid h-9 w-9 place-items-center rounded-full text-ink hover:bg-card" onClick={() => setSidebarOpen(true)} aria-label="Menu">
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/" className="flex items-center gap-2">
              <AppMark size={22} />
              <span className="text-[15px] font-semibold tracking-[-0.01em]">OmniScribe</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="grid h-8 w-8 place-items-center rounded-full bg-accent text-white"
              onClick={() => navigate('/upload')}
              aria-label={t('dashboard.newTranscription')}
              title={t('dashboard.newTranscription')}
            >
              <Plus className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => navigate('/settings')} aria-label={t('nav.settings')}>
              <UserAvatar url={userAvatarUrl} showImage={shouldShowAvatarImage} initials={getUserInitials()} name={user?.name} onError={() => markAvatarFailed(userAvatarUrl)} />
            </button>
          </div>
        </header>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px] modal-backdrop-in" onClick={() => setSidebarOpen(false)} />
            <div className="absolute start-0 top-0 bottom-0 w-[280px] bg-card shadow-window overflow-x-hidden animate-in slide-in-from-left duration-200 rtl:slide-in-from-right">
              {sidebar}
            </div>
          </div>
        )}

        <main className="flex-1 px-4 py-6 lg:px-10 lg:py-8 overflow-x-hidden">
          {children}
        </main>
      </div>

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title={t('app.layout.sidebar.actions.signOut')}
        message={t('auth.signOutConfirmation')}
        confirmText={t('app.layout.sidebar.actions.signOut')}
        cancelText={t('common.cancel')}
        variant="danger"
      />

      <Modal
        isOpen={isCreateFolderOpen}
        onClose={() => { setIsCreateFolderOpen(false); setNewFolderName(''); setFolderFormError(null); }}
        title={t('history.folder.create')}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => { setIsCreateFolderOpen(false); setNewFolderName(''); setFolderFormError(null); }}
              disabled={isCreatingFolder}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateFolder} isLoading={isCreatingFolder} disabled={newFolderName.trim().length === 0}>
              {t('common.create')}
            </Button>
          </>
        }
      >
        <input
          className="w-full rounded-[12px] bg-card px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-accent/40"
          placeholder={t('app.layout.sidebar.folder.createPlaceholder')}
          maxLength={folderNameMaxLength}
          value={newFolderName}
          autoFocus
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void handleCreateFolder(); }}
        />
        <div className="mt-2 flex items-center justify-between text-[12px] text-ink-3">
          <span>{t('history.folder.maxCharacters', { count: folderNameMaxLength })}</span>
          <span className="tnum">{t('history.folder.charactersCount', { current: newFolderNameLength, max: folderNameMaxLength })}</span>
        </div>
        {folderFormError && (
          <div className="mt-2 text-[12.5px] text-[#ff3b30]">
            {folderFormError}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!renameFolder}
        onClose={() => { setRenameFolder(null); setRenameFolderName(''); setFolderFormError(null); }}
        title={t('history.folder.rename')}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => { setRenameFolder(null); setRenameFolderName(''); setFolderFormError(null); }}
              disabled={isRenamingFolder}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleRenameFolder}
              isLoading={isRenamingFolder}
              disabled={renameFolderName.trim().length === 0}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <input
          className="w-full rounded-[12px] bg-card px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-accent/40"
          placeholder={t('app.layout.sidebar.folder.renamePlaceholder')}
          maxLength={folderNameMaxLength}
          value={renameFolderName}
          autoFocus
          onChange={(e) => setRenameFolderName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void handleRenameFolder(); }}
        />
        <div className="mt-2 flex items-center justify-between text-[12px] text-ink-3">
          <span>{t('history.folder.maxCharacters', { count: folderNameMaxLength })}</span>
          <span className="tnum">{t('history.folder.charactersCount', { current: renameFolderNameLength, max: folderNameMaxLength })}</span>
        </div>
        {folderFormError && (
          <div className="mt-2 text-[12.5px] text-[#ff3b30]">
            {folderFormError}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteFolder}
        onClose={() => setDeleteFolder(null)}
        onConfirm={handleDeleteFolder}
        title={t('history.folder.delete')}
        message={t('history.folder.deleteConfirm')}
        confirmText={t('common.delete')}
        variant="danger"
        isLoading={isDeletingFolder}
      />
    </div>
  );
};

// --- Fullscreen Layout (No Sidebar, Minimal Chrome) ---
const FullscreenLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-card flex flex-col">
      <main className="flex-1 p-2 lg:p-4 overflow-x-hidden">
        {children}
      </main>
    </div>
  );
};

export default Layout;
